import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { couponsService, type CreateCouponData } from '@/features/coupons';
import { affiliatesService, type CreateAffiliateData, type UpdateAffiliateData } from '../api/affiliates.service';

function invalidateAffiliates(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.affiliates.all });
}

function invalidateCoupons(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.coupons.all });
}

export function useAffiliateMutations() {
  const qc = useQueryClient();
  const onError = useErrorToast();

  const createAffiliate = useMutation({
    mutationFn: (body: CreateAffiliateData) => affiliatesService.create(body),
    onSuccess: () => {
      invalidateAffiliates(qc);
      toast.success('Afiliado criado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao criar afiliado'),
  });

  const updateAffiliate = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAffiliateData }) => affiliatesService.update(id, body),
    onSuccess: () => {
      invalidateAffiliates(qc);
      toast.success('Afiliado atualizado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao atualizar afiliado'),
  });

  const deleteAffiliate = useMutation({
    mutationFn: (id: string) => affiliatesService.delete(id),
    onSuccess: () => {
      invalidateAffiliates(qc);
      toast.success('Afiliado excluído.');
    },
    onError: (e) => onError(e, 'Erro ao excluir afiliado'),
  });

  const createAffiliateCoupon = useMutation({
    mutationFn: (input: { affiliateId: string; formData: CreateCouponData }) =>
      couponsService.create({
        ...input.formData,
        code: input.formData.code.trim().toUpperCase(),
        usageType: 'AFFILIATE',
        affiliateId: input.affiliateId,
      }),
    onSuccess: (_data, variables) => {
      invalidateAffiliates(qc);
      invalidateCoupons(qc);
      void qc.invalidateQueries({ queryKey: queryKeys.coupons.list({ affiliateId: variables.affiliateId }) });
      toast.success('Cupom criado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao criar cupom'),
  });

  return { createAffiliate, updateAffiliate, deleteAffiliate, createAffiliateCoupon };
}
